import { BadgeProps } from "@radix-ui/themes";
import { TrackingItemType } from "../../api";
import { SelectProps } from "../../components";
import { getDateFromString, getDateTimeFromString } from "../../functions";

enum DeliveryTypes {
  DELIVERY = "delivery",
  PICKUP = "pickup",
}

const PICKUP_NUMBERS = [9, 31, 75, 99, 916];

const getDeliveryType = (shippingMethodID?: number): DeliveryTypes => {
  if (!shippingMethodID) {
    return DeliveryTypes.DELIVERY;
  }
  if (PICKUP_NUMBERS.includes(shippingMethodID)) {
    return DeliveryTypes.PICKUP;
  }
  return DeliveryTypes.DELIVERY;
};

const deliveryStatusLabels: Record<number, string> = {
  1: "Bestellung ist eingegangen",
  2: "Ware ist im Rückstand und beim Lieferanten bestellt",
  3: "Ware im Lager reserviert",
  4: "Ware ist im Lager eingetroffen",
  5: "Ware wurde kommissioniert",
  6: "Ware ist versandfertig",
  7: "Ware wurde angeliefert",
};

const deliveryStatusLabelsPickup: Record<number, string> = {
  1: deliveryStatusLabels[1],
  2: deliveryStatusLabels[2],
  3: deliveryStatusLabels[3],
  4: deliveryStatusLabels[4],
  5: deliveryStatusLabels[5],
  6: "Abholung",
};

export const deliveryFilterState: Record<
  number,
  { text: string; color: BadgeProps["color"] }
> = {
  1: { text: "offen", color: "brown" },
  2: { text: "in Bearbeitung", color: "yellow" },
  3: { text: "geliefert", color: "green" },
};

const deliveryStatusDetails: Record<
  number,
  { infoText: string; simpleDeliveryState: number; deliveryFilterState: number }
> = {
  1: {
    infoText:
      "Streckenlieferung: wird für Sie erst noch bestellt und wird direkt vom Lieferanten geliefert",
    simpleDeliveryState: 1,
    deliveryFilterState: 1,
  },
  2: {
    infoText:
      "Streckenlieferung: ist bestellt und wird direkt vom Lieferanten geliefert, es liegt uns kein bestätigter Liefertermin vor",
    simpleDeliveryState: 2,
    deliveryFilterState: 1,
  },
  3: {
    infoText:
      "Streckenlieferung: wird direkt vom Lieferanten geliefert, voraussichtlicher Liefertermin: {displayDeliveryDate}",
    simpleDeliveryState: 2,
    deliveryFilterState: 1,
  },
  4: {
    infoText:
      "Lagerware: ist nachbestellt, Voraussichtlicher Liefertermin: {displayDeliveryDate}",
    simpleDeliveryState: 2,
    deliveryFilterState: 1,
  },
  5: {
    infoText:
      "Lagerware: ist rückständig, es liegt kein bestätigter Liefertermin des Vorlieferanten vor",
    simpleDeliveryState: 1,
    deliveryFilterState: 1,
  },
  6: {
    infoText: "Beschaffungsware - wird für Sie erst noch bestellt",
    simpleDeliveryState: 1,
    deliveryFilterState: 1,
  },
  7: {
    infoText:
      "Beschaffungsware - ist bestellt, es liegt uns kein bestätigter Liefertermin vor",
    simpleDeliveryState: 2,
    deliveryFilterState: 1,
  },
  8: {
    infoText:
      "Beschaffungsware - Voraussichtlicher Liefertermin: {displayDeliveryDate}",
    simpleDeliveryState: 2,
    deliveryFilterState: 1,
  },
  9: {
    infoText: "Lagerware: ist für Sie reserviert",
    simpleDeliveryState: 3,
    deliveryFilterState: 1,
  },
  10: {
    infoText:
      "Lagerware: {amountDelivered} {salesUnit} sind reserviert, weitere {amountPending} werden beschafft",
    simpleDeliveryState: 3,
    deliveryFilterState: 1,
  },
  11: {
    infoText: "Beschaffungsware - Ware liegt zur Auslieferung am Lager bereit",
    simpleDeliveryState: 4,
    deliveryFilterState: 1,
  },
  12: {
    infoText: "Abholung: Ware liegt zur Abholung bereit",
    simpleDeliveryState: 6,
    deliveryFilterState: 2,
  },
  13: {
    infoText:
      "Abholung: {amountDelivered} {salesUnit} liegen zur Abholung bereit, weitere {amountPending} sind noch offen",
    simpleDeliveryState: 6,
    deliveryFilterState: 2,
  },
  14: {
    infoText: "Kommissionierung läuft",
    simpleDeliveryState: 5,
    deliveryFilterState: 1,
  },
  15: {
    infoText:
      "Kommissionierung von {amountDelivered} {salesUnit} läuft, weitere {amountPending} sind noch offen",
    simpleDeliveryState: 5,
    deliveryFilterState: 1,
  },
  16: {
    infoText: "Wurde vom Lieferanten direkt an Sie geliefert",
    simpleDeliveryState: 7,
    deliveryFilterState: 3,
  },
  17: {
    infoText: "Auslieferung ist erfolgt per LKW\n{habblDeliveryDate}",
    simpleDeliveryState: 7,
    deliveryFilterState: 3,
  },
  18: {
    infoText: "Auslieferung ist erfolgt per Spedition",
    simpleDeliveryState: 7,
    deliveryFilterState: 3,
  },
  19: {
    infoText: "Auslieferung ist erfolgt per Paketdienst",
    simpleDeliveryState: 7,
    deliveryFilterState: 3,
  },
  20: {
    infoText: "Lieferschein ist gedruckt",
    simpleDeliveryState: 6,
    deliveryFilterState: 1,
  },
  21: {
    infoText:
      "Lieferschein ist gedruckt mit Liefermenge {amountDelivered} {salesUnit}",
    simpleDeliveryState: 6,
    deliveryFilterState: 2,
  },
  22: {
    infoText: "Lieferschein ist gedruckt, Ware ist in Auslieferung",
    simpleDeliveryState: 6,
    deliveryFilterState: 2,
  },
  23: {
    infoText:
      "Lieferschein ist gedruckt, {amountDelivered} {salesUnit} sind in Auslieferung",
    simpleDeliveryState: 6,
    deliveryFilterState: 1,
  },
  24: {
    infoText:
      "Beschaffungsware: die Auftragsposition wird zu ihrem Wunschdatum {displayDeliveryDate} beschafft",
    simpleDeliveryState: 2,
    deliveryFilterState: 2,
  },
  25: {
    infoText: "Ware wurde der Spedition übergeben",
    simpleDeliveryState: 7,
    deliveryFilterState: 3,
  },
  26: {
    infoText: "Ware wurde dem Paketdienst übergeben",
    simpleDeliveryState: 7,
    deliveryFilterState: 3,
  },
};

export const getInfoText = (
  props: TrackingItemType,
  displayDeliveryDate?: string,
): string => {
  const {
    deliveryStatus,
    orderNo,
    salesUnit,
    amountDelivered,
    amountPending,
    dateExpected,
    requestedDeliveryDate,
    dateStorage,
    habblDeliveryDate,
  } = props;
  const delivery = deliveryStatusDetails[deliveryStatus];
  if (!delivery) {
    return "Lieferstatus nicht bekannt";
  }
  return delivery.infoText
    .replace("{orderNo}", orderNo?.toString() || "")
    .replace("{salesUnit}", salesUnit || "")
    .replace("{amountDelivered}", amountDelivered?.toString() || "")
    .replace("{amountPending}", amountPending?.toString() || "")
    .replace("{dateExpected}", getDateFromString(dateExpected))
    .replace(
      "{requestedDeliveryDate}",
      getDateFromString(requestedDeliveryDate),
    )
    .replace("{dateStorage}", getDateFromString(dateStorage))
    .replace("{displayDeliveryDate}", getDateFromString(displayDeliveryDate))
    .replace("{habblDeliveryDate}", getDateTimeFromString(habblDeliveryDate));
};

export const getSimpleText = (
  deliveryStatus: number,
  shippingMethodID?: number,
): string => {
  const delivery = deliveryStatusDetails[deliveryStatus];
  if (!delivery) {
    return "";
  }
  let deliveryStatusLabel = deliveryStatusLabels[delivery.simpleDeliveryState];
  if (getDeliveryType(shippingMethodID) === DeliveryTypes.PICKUP) {
    deliveryStatusLabel =
      deliveryStatusLabelsPickup[delivery.simpleDeliveryState];
  }
  if (!deliveryStatusLabel) {
    return "";
  }
  return deliveryStatusLabel;
};

export const getBadgeText = (deliveryStatus: number): string => {
  const delivery = deliveryStatusDetails[deliveryStatus];
  if (!delivery) {
    return "";
  }
  const deliveryFilterItem = deliveryFilterState[delivery.deliveryFilterState];
  if (!deliveryFilterItem) {
    return "";
  }
  return deliveryFilterItem.text;
};

export const getSimpleStep = (deliveryStatus: number): number => {
  const delivery = deliveryStatusDetails[deliveryStatus];
  if (!delivery) {
    return 0;
  }
  return delivery.simpleDeliveryState;
};

export const getMaxStep = (shippingMethodID?: number) => {
  if (getDeliveryType(shippingMethodID) === DeliveryTypes.PICKUP) {
    return Object.keys(deliveryStatusLabelsPickup).length;
  }
  return Object.keys(deliveryStatusLabels).length;
};

export const getSimpleStateSelectOptions = () => {
  const selectItems: SelectProps["items"] = [
    { value: "all", label: "Alle anzeigen" },
    ...Object.entries(deliveryFilterState).map(([, value]) => ({
      value: value.text,
      label: value.text,
    })),
  ];

  return selectItems;
};
